import { diff as objectUtilsDiff } from '../utils/object-utils/object-utils';

export function _createSaveEmbedded($q) {
  /**
   * Creates a method that can be used after a normal save to automatically save an embedded
   * array of children on a parent model.
   *
   * @param options {Object|Array<{Model,fieldName,foreignKey,changes,orderable}>}
   * @param options.Model {Resource|Array<Array>} JS-Data resource of the children.
   *        If this is an array, each item is passed as an argument to saveEmbedded
   * @param options.fieldName {String?} Name of the field for the children on the parent object
   * @param options.foreignKey {String?} Name of the reference field to point to the parent's ID on the child
   * @param options.changes {*?} Optional set of changes on the parent model, to prevent unnecessary calls out
   * @param options.orderable {boolean?}
   * @param options.exclude {Function?} if provided, used to exclude changes on the embedded
   * @param options.diff {Function?} if provided, use instead of object-utils.diff for determining changes
   * @param options.uniqueChildField {String?} If provided, use to prevent uniqueness constraint errors
   * @param options.copyFields {Array<String>?} If provided, copy these values from changes to any modified child
   * @param options.requestOptions {*?} Params to send along with the request
   * @returns {Function} Function to call with the parent model, usually used in a `.then` after a save or update. Parent instance is returned.
   */
  return function saveEmbedded(options) {
    if (Array.isArray(options)) {
      return function (parent) {
        return $q
          .all(options.map((options) => saveEmbedded(options)(parent)))
          .then(() => parent);
      };
    }
    const {
      Model,
      fieldName,
      foreignKey = null,
      changes = null,
      requestOptions = {},
      orderable = true,
      exclude = angular.noop,
      uniqueChildField = null,
      copyFields = ['tag'],
      diff = objectUtilsDiff,
    } = options;
    const baseFields = () => {
      let fields = {};
      if (changes) {
        copyFields.forEach((f) => {
          fields[f] = changes[f];
        });
      }
      return fields;
    };
    return function (parent) {
      let updates = changes && changes[fieldName];
      if (!updates && (changes || !parent[fieldName])) {
        // don't bother if we have no changes to report
        return $q.resolve(parent);
      }

      const _addForeignKeyParam = (options) => {
        if (foreignKey) {
          if (options) {
            options[foreignKey] = parent.id;
          } else {
            options = angular.copy(requestOptions);
            options.params = options.params || {};
            options.params[foreignKey] = parent.id;
          }
        }
        return options || requestOptions;
      };

      // handle deletions first (for unique constraints)
      let delPromises = $q.when();
      if (updates && parent[fieldName] && parent[fieldName].length) {
        if (uniqueChildField) {
          // Delete the IDs of any updated items that would cause uniqueness constraint issues (they'll
          // be deleted and recreated instead).
          // This happens, for example, if two files have swapped paths.
          updates.forEach((newChild) => {
            const oldChild = parent[fieldName].find(
              (oldChild) =>
                oldChild.id !== newChild.id &&
                oldChild[uniqueChildField] === newChild[uniqueChildField],
            );
            if (oldChild) {
              delete newChild.id;
              const updateToOldChild = updates.find(
                (nc) => nc.id === oldChild.id,
              );
              if (updateToOldChild) {
                // If only one child was renamed, it's possible to get into a scenario where the
                // updates happen out-of-order, causing a uniqueness constraint, so we go ahead
                // and treat that file as deleted-and-recreated as well.
                delete updateToOldChild.id;
              }
            }
          });
        }
        delPromises = $q.all(
          parent[fieldName]
            .filter(
              (oldChild) =>
                oldChild.id &&
                !oldChild.softDeletedAt &&
                updates.every((newChild) => oldChild.id !== newChild.id),
            )
            .map((oldChild) => {
              return Model.destroy(oldChild, _addForeignKeyParam());
            }),
        );
      }

      return delPromises
        .then(() => {
          // Save or create any changed or new child models
          let promises;
          promises = (updates || parent[fieldName]).map((child, i) => {
            // Ensure files with softDeletedAt are included
            if (exclude(child) && !child.softDeletedAt) {
              // return the internally stored file if we don't save changes here
              return Model.get(child.id);
            }
            if (orderable) {
              child.position = i + 1;
            }
            const createModel = () => {
              const params = _addForeignKeyParam(baseFields());
              angular.extend(params, child);
              delete params.id;
              return Model.create(params, requestOptions);
            };
            if (child.id) {
              let childOrig = Model.get(child.id);
              let updatePromise;
              if (childOrig) {
                let childCopy = Object.assign({}, child);
                // strip any special $ prefixed values out of the object to do a proper diff
                Object.keys(childCopy)
                  .filter((k) => k[0] === '$')
                  .forEach((key) => {
                    delete childCopy[key];
                  });

                let diffData = diff(childOrig, childCopy);

                if (diffData.$empty) {
                  // no need to update child, just return the local reference
                  return childOrig;
                } else {
                  // always include type
                  diffData.$type = childOrig.$type;
                  angular.extend(diffData, _addForeignKeyParam(baseFields()));
                  updatePromise = Model.update(
                    childOrig,
                    diffData,
                    requestOptions,
                  );
                }
              } else {
                // no local copy?
                const params = angular.extend(
                  _addForeignKeyParam(baseFields()),
                  child,
                );
                updatePromise = Model.update(child.id, params, requestOptions);
              }
              return updatePromise.catch((resp) => {
                // on 404 errors, just create a new file instead
                return resp.status === 404 ? createModel() : $q.reject(resp);
              });
            } else {
              // save a new one
              return createModel();
            }
          });

          // Wait for any and all changes
          return $q.all(promises);
        })
        .then((children) => {
          // Clean up any old children on the parent model missed above
          if (parent[fieldName] && parent[fieldName].length) {
            return $q
              .all(
                parent[fieldName]
                  .filter(
                    (oldChild) =>
                      !oldChild.softDeletedAt &&
                      children.every((newChild) => newChild.id !== oldChild.id),
                  )
                  .map((oldChild) => {
                    return (
                      oldChild.id &&
                      Model.destroy(oldChild, _addForeignKeyParam()).catch(
                        (resp) => {
                          // ignore 404 errors on deleted embedded saves
                          return resp.status === 404 ? true : $q.reject(resp);
                        },
                      )
                    );
                  }),
              )
              .then(() => children);
          }
          return children;
        })
        .then((children) => {
          if (parent[fieldName]) {
            // replace the array of children with the updated models
            parent[fieldName].length = 0;
            parent[fieldName].push(...children);
            return parent;
          }
        });
    };
  };
}
